import { useQuery } from "@apollo/client";
import Business from "@mui/icons-material/Business";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import {
  AssociateFacilitiesDialog,
  FacilityRow,
} from "components/AssociateFacilities/AssociateFacilitiesDialog";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import {
  getWasteSubmissionFormName,
  getWasteSubmissionFormNameForMethods,
  runsInteractiveStepper,
} from "encamp-shared/src/submissions/wasteSubmissionForms";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  SubmissionMethod,
  SubmissionType,
  WasteSubmissionFormOptionsQuery,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";

import { isSubmissionMethod } from "./submissionEnumGuards";

export type CreateStepperArgs = {
  submissionType: SubmissionType;
  submissionMethod: SubmissionMethod;
  facilityIds: string[];
  title: string;
  dueDate: DateTime;
};

type FormOption = WasteSubmissionFormOptionsQuery["wasteSubmissionFormOptions"][number];

const WASTE_SUBMISSION_FORM_OPTIONS_QUERY = gql(`
  query WasteSubmissionFormOptions($facilityIds: [ID!]!) {
    wasteSubmissionFormOptions(facilityIds: $facilityIds) {
      submissionType
      submissionMethods
      recurring
      stateSpecific
    }
  }
`);

const FORM_LIST_HEIGHT = 320;

/** Single-line address summary for a selected facility, e.g. "123 Main St, Austin, TX 78701". */
function facilityAddress(facility: FacilityRow): string {
  const { streetAddress1, city, state, zip } = facility;
  return [streetAddress1, city, state, zip].filter(Boolean).join(", ");
}

// Named from the methods the row still offers, so it can't promise a form none
// of them files.
function formName(option: FormOption): string | undefined {
  return getWasteSubmissionFormNameForMethods(option.submissionType, option.submissionMethods);
}

function formLabel(option: FormOption): string {
  return formName(option) ?? prettyPrintEnumValue(option.submissionType);
}

function formCaption(option: FormOption): string {
  return formName(option)
    ? prettyPrintEnumValue(option.submissionType)
    : "Choose how this is filed below";
}

function optionKey(option: FormOption): string {
  return option.submissionType;
}

function enabledSubmissionMethods(
  option: FormOption,
  showStepperOption: boolean,
  showPaperFormOption: boolean
): SubmissionMethod[] {
  return option.submissionMethods
    .filter((submissionMethod) => {
      // With the interactive stepper off, an interactive combo (8700-12) runs
      // the document flow rather than disappearing — so the paper-form flag
      // alone is enough to offer any method.
      const interactive = runsInteractiveStepper(
        option.submissionType,
        submissionMethod,
        showStepperOption
      );
      return interactive || showPaperFormOption;
    })
    .sort((a, b) => prettyPrintEnumValue(a).localeCompare(prettyPrintEnumValue(b)));
}

/** Selected-facilities summary box, or the initial "Select facilities" button. */
function FacilitiesField({
  facilityIds,
  facilities,
  onOpenPicker,
}: {
  facilityIds: string[];
  facilities: FacilityRow[];
  onOpenPicker: () => void;
}) {
  const theme = useTheme();

  if (facilityIds.length === 0) {
    return (
      <Box>
        <Button variant="outlined" startIcon={<Business />} onClick={onOpenPicker}>
          Select facilities
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        p: 1.5,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        {facilities.map((facility) => (
          <Box key={facility.id}>
            <Typography variant="body2" fontWeight="medium" noWrap>
              {facility.name}
            </Typography>
            {facilityAddress(facility) && (
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {facilityAddress(facility)}
              </Typography>
            )}
          </Box>
        ))}
        {facilityIds.length > facilities.length && (
          <Typography variant="body2" fontWeight="medium">
            {facilityIds.length - facilities.length} more selected
          </Typography>
        )}
      </Box>
      <Button size="small" onClick={onOpenPicker}>
        Change
      </Button>
    </Box>
  );
}

/** Loading / error / empty-list / radio-group states for the form picker. */
function FormOptionsPicker({
  loading,
  error,
  options,
  groups,
  selectedKey,
  onSelectChange,
}: {
  loading: boolean;
  error: unknown;
  options: FormOption[];
  groups: { title: string; options: FormOption[] }[];
  selectedKey: string;
  onSelectChange: (key: string) => void;
}) {
  if (loading) {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Loading forms…
        </Typography>
      </Stack>
    );
  }

  // A failed query would otherwise read as a legitimately empty list.
  if (error) {
    return <Alert severity="error">Forms could not be loaded. Try again.</Alert>;
  }

  if (options.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No forms are available for every selected facility. Change the facility selection and try
        again.
      </Typography>
    );
  }

  return (
    <RadioGroup
      aria-label="Form"
      value={selectedKey}
      onChange={(e) => onSelectChange(e.target.value)}
    >
      {groups.map((group) => (
        // FormControlLabel is inline-flex; without a flex column the radios don't stack.
        <Box
          key={group.title}
          sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", mb: 1 }}
        >
          <Typography variant="overline" color="text.secondary">
            {group.title}
          </Typography>
          {group.options.map((option) => (
            <FormControlLabel
              key={optionKey(option)}
              value={optionKey(option)}
              control={<Radio />}
              sx={{ alignItems: "flex-start", mb: 0.5, "& .MuiRadio-root": { pt: 0 } }}
              label={
                <Stack>
                  <Typography variant="body1">{formLabel(option)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formCaption(option)}
                  </Typography>
                </Stack>
              }
            />
          ))}
        </Box>
      ))}
    </RadioGroup>
  );
}

// Named from the chosen combo, the same way the stepper's Understand step names
// it, so the title can't contradict the form the user lands in.
function deriveTitle(
  facilities: FacilityRow[],
  facilityCount: number,
  submissionType: SubmissionType,
  submissionMethod: SubmissionMethod
): string {
  const form =
    getWasteSubmissionFormName(submissionType, submissionMethod) ??
    prettyPrintEnumValue(submissionType);
  const [first] = facilities;
  if (facilityCount === 1 && first) {
    return `${first.name}: ${form}`;
  }
  const facilityLabel = facilityCount === 1 ? "facility" : "facilities";
  return `${form} — ${facilityCount} ${facilityLabel}`;
}

/**
 * Facilities first, because their states determine which forms exist — a selection
 * spanning states offers only the forms every one of them files. The selected
 * form determines the submission type; the user chooses a state-relevant filing
 * method, which in turn determines whether the interactive or document flow runs.
 */
export function CreateSubmissionTypeDialog({
  open,
  onClose,
  onCreateStepper,
  creating = false,
  showStepperOption = true,
  showPaperFormOption = false,
}: {
  open: boolean;
  onClose: () => void;
  onCreateStepper: (args: CreateStepperArgs) => void;
  creating?: boolean;
  showStepperOption?: boolean;
  showPaperFormOption?: boolean;
}) {
  const theme = useTheme();
  // Ids are the source of truth; the rows are for display and may not cover every
  // id (the picker only hands back rows its grid loaded).
  const [facilityIds, setFacilityIds] = useState<string[]>([]);
  const [facilityRows, setFacilityRows] = useState<FacilityRow[]>([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [chosenMethod, setChosenMethod] = useState<SubmissionMethod | "">("");
  const [dueDate, setDueDate] = useState<DateTime | null>(null);
  const [facilityDialogOpen, setFacilityDialogOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const facilities = useMemo(
    () =>
      facilityIds.flatMap((id) => {
        const row = facilityRows.find((candidate) => candidate.id === id);
        return row ? [row] : [];
      }),
    [facilityIds, facilityRows]
  );
  const states = useMemo(
    () => [...new Set(facilities.map((f) => f.state).filter(Boolean))],
    [facilities]
  );

  const { data, loading, error } = useQuery(WASTE_SUBMISSION_FORM_OPTIONS_QUERY, {
    variables: { facilityIds },
    skip: facilityIds.length === 0,
  });

  // Keep only methods whose flow is enabled, then hide forms with no usable
  // method. The chosen combo, rather than the type alone, determines the flow.
  const options = useMemo(
    () =>
      (data?.wasteSubmissionFormOptions ?? []).flatMap((option) => {
        const submissionMethods = enabledSubmissionMethods(
          option,
          showStepperOption,
          showPaperFormOption
        );
        return submissionMethods.length > 0 ? [{ ...option, submissionMethods }] : [];
      }),
    [data?.wasteSubmissionFormOptions, showStepperOption, showPaperFormOption]
  );

  const groups = useMemo(() => {
    const [onlyState] = states;
    const stateLabel =
      states.length === 1 && onlyState ? `Other ${onlyState} forms` : "State forms";
    // Alphabetical by the label the user reads, with the type breaking ties
    // between two types of the same named form (the annual and biennial 8700-13).
    const byLabel = (a: FormOption, b: FormOption) => {
      const byForm = formLabel(a).localeCompare(formLabel(b));
      if (byForm !== 0) {
        return byForm;
      }
      return prettyPrintEnumValue(a.submissionType).localeCompare(
        prettyPrintEnumValue(b.submissionType)
      );
    };
    return [
      {
        title: "Recurring reports",
        options: options.filter((option) => option.recurring),
      },
      {
        title: stateLabel,
        options: options.filter((option) => !option.recurring && option.stateSpecific),
      },
      {
        title: "One-off forms",
        options: options.filter((option) => !option.recurring && !option.stateSpecific),
      },
    ]
      .filter((group) => group.options.length > 0)
      .map((group) => ({ ...group, options: group.options.sort(byLabel) }));
  }, [options, states]);

  const selected = options.find((option) => optionKey(option) === selectedKey) ?? null;
  const submissionMethod = chosenMethod || null;
  const canCreate = facilityIds.length > 0 && !!selected && !!submissionMethod && !!dueDate;

  const reset = () => {
    setFacilityIds([]);
    setFacilityRows([]);
    setSelectedKey("");
    setChosenMethod("");
    setDueDate(null);
    setFacilityDialogOpen(false);
  };

  const discardAndClose = () => {
    reset();
    onClose();
  };

  // Closing discards everything, and the backdrop is easy to hit while reaching
  // for a picker. Cancel and the X are deliberate, so only the accidental ways
  // out ask first — and only once something would actually be lost.
  const hasEntries =
    facilityIds.length > 0 || selectedKey !== "" || chosenMethod !== "" || !!dueDate;

  const handleClose = (
    _event?: unknown,
    reason?: "backdropClick" | "escapeKeyDown" | "cancelButtonClick" | "closeButtonClick"
  ) => {
    if (hasEntries && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      setDiscardConfirmOpen(true);
      return;
    }
    discardAndClose();
  };

  const handleCreate = () => {
    if (facilityIds.length === 0 || !selected || !submissionMethod || !dueDate) {
      return;
    }
    onCreateStepper({
      submissionType: selected.submissionType,
      submissionMethod,
      facilityIds,
      title: deriveTitle(facilities, facilityIds.length, selected.submissionType, submissionMethod),
      dueDate,
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        title="Create Submission"
        description="Choose the facilities, then the form you're filing"
        primaryActionButton={
          // Creating a submission is staff-only: staff orange.
          <Button
            variant="contained"
            color="secondary"
            loading={creating}
            disabled={!canCreate}
            onClick={handleCreate}
          >
            Continue
          </Button>
        }
      >
        {/* Facility selector mirrors the document-review picker (omnisearch by
          name, address, county, tags, …) — name-only search isn't enough for
          larger tenants. */}
        <Box>
          <Typography variant="caption" color="text.secondary">
            Facilities *
          </Typography>
          <FacilitiesField
            facilityIds={facilityIds}
            facilities={facilities}
            onOpenPicker={() => setFacilityDialogOpen(true)}
          />
        </Box>

        {states.length > 1 && (
          <Typography variant="caption" color="text.secondary">
            These facilities are in {states.join(", ")}, so only forms filed in every one of those
            states are offered.
          </Typography>
        )}

        {/* Fixed height once there is a list, so switching facilities doesn't resize the dialog. */}
        <Box
          sx={{
            height: facilityIds.length > 0 ? FORM_LIST_HEIGHT : undefined,
            overflowY: "auto",
            borderTop: facilityIds.length > 0 ? `1px solid ${theme.palette.divider}` : undefined,
            borderBottom: facilityIds.length > 0 ? `1px solid ${theme.palette.divider}` : undefined,
            py: facilityIds.length > 0 ? 1 : 0,
          }}
        >
          {facilityIds.length > 0 && (
            <FormOptionsPicker
              loading={loading}
              error={error}
              options={options}
              groups={groups}
              selectedKey={selectedKey}
              onSelectChange={(key) => {
                setSelectedKey(key);
                setChosenMethod("");
              }}
            />
          )}
        </Box>

        {facilityIds.length > 0 && (
          <FormControl fullWidth data-testid="submission-method-control">
            <InputLabel id="submission-method-label">Submission method *</InputLabel>
            <Select
              labelId="submission-method-label"
              label="Submission method *"
              value={chosenMethod}
              disabled={!selected}
              onChange={(e) => {
                if (isSubmissionMethod(e.target.value)) {
                  setChosenMethod(e.target.value);
                }
              }}
            >
              {(selected?.submissionMethods ?? []).map((method) => (
                <MenuItem key={method} value={method}>
                  {prettyPrintEnumValue(method)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <DatePicker
          label="Due date *"
          value={dueDate}
          onChange={setDueDate}
          slotProps={{ textField: { fullWidth: true } }}
        />

        <AssociateFacilitiesDialog
          open={facilityDialogOpen}
          onClose={() => setFacilityDialogOpen(false)}
          existingFacilityIds={facilityIds}
          onSave={(ids) => {
            setFacilityIds(ids);
            setSelectedKey("");
            setChosenMethod("");
          }}
          onSaveRows={setFacilityRows}
          subtitle="Select the facilities this submission covers."
          excludeSecondaryWasteFacilities
        />
      </Dialog>

      {discardConfirmOpen && (
        <ConfirmDialog
          open={discardConfirmOpen}
          onClose={() => setDiscardConfirmOpen(false)}
          onConfirm={() => {
            setDiscardConfirmOpen(false);
            discardAndClose();
          }}
          // Keep this narrower than the parent creation dialog.
          maxWidth="520px"
          header="You have unsaved changes. Are you sure you want to close?"
          info="The submission has not been created yet, so any progress you made will be lost."
          confirmText="Yes, I'm Sure"
        />
      )}
    </>
  );
}
